<script setup lang="ts">
    import { computed, inject, onMounted, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import EmptyState from '@shared/components/EmptyState.vue';
    import Modal from '@shared/components/Modal.vue';
    import SingleSelect from '@shared/components/SingleSelect.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { ScopeAccess } from '@shared/directives/Scope.access';
    import { Permissions } from '@shared/Root/Permissions';
    import { useObjectTracking, type OthersJoined } from '@shared/utils/use-object-tracking';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import ContactModal from '@views/Contact/ContactModal.vue';
    import { type ContactData } from '@views/Contact/ContactTypes';
    import EventListItemFullStatus from '@views/Event/EventListItemFullStatus.vue';
    import { type Event, EventStatusNames } from '@views/Event/EventTypes';
    import { loadLists } from '@views/Lists/Lists.access';
    import { type List as ContactListType } from '@views/Lists/ListsTypes';
    import GuestListToolbar from './GuestList/GuestListToolbar.vue';
    import GuestListTable from './GuestList/GuestListTable.vue';
    import GuestResponseModal from './GuestList/GuestResponseModal.vue';
    import { useGuestList } from './GuestList/use-guest-list';
    import { type EventParticipant, type FullParticipantStatus, type GuestRow, type ParticipantStatusName } from './GuestList/guest-list-types';
    import * as guestListApi from './GuestList/EventGuestList.access';
    import { eventKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const event = inject(eventKey)!;
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const router = useRouter();

    const list = useGuestList(() => props.eventId);

    // Informs (without restricting anything) when another user or tab has the same guest list open.
    useObjectTracking(() => props.eventId, notifyOthersJoined);

    function notifyOthersJoined({ otherEditors, otherTabsCount }: OthersJoined) {
        const lines: string[] = [];
        if (otherEditors.length > 0) {
            const names = otherEditors.map((e) => `${e.name} (${e.userName})`).join(', ');
            lines.push(`${$Resources.EventGuestListOtherActiveClientsOtherUsers}: ${names}`);
        }
        if (otherTabsCount > 0) lines.push(($Resources.EventGuestListOtherActiveClientsOtherTabs ?? '').replace('{}', String(otherTabsCount)));

        bsAlert({ type: AlertTypes.Warning, title: $Resources.EventGuestListOtherActiveClientsWarning, content: lines.join(' '), timeout: false });
    }

    const requireAttestation = computed(() => !!event.value?.RequireAttestation);
    const canReadContacts = computed(() => scopeAccess.hasScope({ Feature: 'Contacts', Permission: Permissions.Read }));

    const statusLoaded = ref(false);
    const participantTotal = ref(0);
    const acceptedTotal = ref(0);
    const attestationPendingTotal = ref(0);

    // The bulk change applies the search, so with a search active only the matching accepted guests are affected.
    const bulkAcceptedCount = computed(() => list.acceptedMatchingCount.value ?? acceptedTotal.value);

    function onStatusLoaded(status: FullParticipantStatus) {
        statusLoaded.value = true;
        participantTotal.value = status.All;
        acceptedTotal.value = status.Accepted;
        attestationPendingTotal.value = status.AttestationPending;
    }

    const disableStatusNotifications = ref(false);
    const busy = ref(false);
    const contactModalId = ref<string | null>(null);

    onMounted(() => list.reset());

    async function run(work: () => Promise<unknown>, onSuccess?: () => void, errorTitle = $Resources.ErrorHeading, errorContent?: string) {
        busy.value = true;
        try {
            await work();
            onSuccess?.();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: errorTitle, content: errorContent });
        } finally {
            busy.value = false;
        }
    }

    // --- Status changes ---

    const updateStatus = (participant: EventParticipant, status: ParticipantStatusName) =>
        run(
            () => guestListApi.updateParticipantStatus(props.eventId, participant.EventParticipantId, status, disableStatusNotifications.value),
            () => list.refreshGroups([participant.EventParticipantId])
        );

    const selectedToAttended = () => {
        const selected = list.selectedIds.value;
        return run(
            () => guestListApi.statusToAttended(props.eventId, selected),
            () => {
                bsAlert({ title: $Resources.AlertSuccessHeading });
                list.clearSelection();
                list.refreshGroups(selected);
            }
        );
    };

    const bulkToAttended = () =>
        run(
            () =>
                guestListApi.bulkStatusChange(props.eventId, {
                    TargetStatus: 'Attended',
                    SourceStatusFilter: 'Accepted',
                    FreeTextFilter: list.searchTerm.value,
                }),
            () => {
                bsAlert({ title: $Resources.AlertSuccessHeading });
                list.clearSelection();
                list.reloadCurrent();
            }
        );

    // --- Mails ---

    const resendConfirmation = (participant: EventParticipant) =>
        run(
            () => guestListApi.sendConfirmationMail(props.eventId, participant.EventParticipantId, participant.StatusName),
            () => bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.AlertSuccessConfirmationSendBody }),
            $Resources.ErrorHeading,
            $Resources.AlertErrorConfirmationSendBody
        );

    const resendAttestation = (participant: EventParticipant) =>
        run(
            () => guestListApi.sendAttestationMail(props.eventId, participant.EventParticipantId),
            () => bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.alertSuccessAttestationSendBody }),
            $Resources.ErrorHeading,
            $Resources.alertErrorAttestationSendBody
        );

    const resendAllAttestations = () =>
        run(
            () => guestListApi.sendMultipleAttestationMail(props.eventId),
            () => bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.alertSuccessAttestationSendBody }),
            $Resources.ErrorHeading,
            $Resources.alertErrorAttestationSendBody
        );

    // --- Confirmations ---

    interface PendingConfirm {
        title: string;
        text?: string;
        participant?: EventParticipant;
        action: () => unknown;
    }

    const pendingConfirm = ref<PendingConfirm | null>(null);

    function confirmPending() {
        const action = pendingConfirm.value?.action;
        pendingConfirm.value = null;
        return action?.();
    }

    const participantName = (p: EventParticipant) => [p.FirstName, p.LastName].filter(Boolean).join(' ');

    const confirmBulkToAttended = () =>
        (pendingConfirm.value = {
            title: $Resources.EventGuestListBulkStatusToAttended,
            text: $Resources.EventGuestListBulkStatusUpdateConfirmText.replace('{Count}', String(bulkAcceptedCount.value)).replace(
                '{NewStatus}',
                $Resources.EventGuestListAttendedLabel.toLowerCase()
            ),
            action: bulkToAttended,
        });

    const confirmResendAllAttestations = () =>
        (pendingConfirm.value = {
            title: $Resources.ConfirmButton,
            text: $Resources.EventGuestListResendAllAttestation,
            action: resendAllAttestations,
        });

    const confirmResendConfirmation = (participant: EventParticipant) =>
        (pendingConfirm.value = {
            title: $Resources.EventGuestlistResendConfirmation,
            participant,
            action: () => resendConfirmation(participant),
        });

    const confirmResendAttestation = (participant: EventParticipant) =>
        (pendingConfirm.value = {
            title: $Resources.EventGuestListResendAttestation,
            participant,
            action: () => resendAttestation(participant),
        });

    // --- Export ---

    const runExport = (work: () => Promise<unknown>) => run(work, undefined, $Resources.ExportFailedHeading, $Resources.ExportFailedBody);

    const exportToFile = () =>
        runExport(async () => {
            window.location.href = (await guestListApi.exportParticipantsToFile(props.eventId, list.status.value)).Url;
        });

    const exportToCsv = () =>
        runExport(async () => {
            window.location.href = (await guestListApi.exportParticipantsToCsv(props.eventId, list.status.value)).Url;
        });

    const exportToNewList = () =>
        runExport(async () => {
            const { ListId } = await guestListApi.exportParticipantsToNewList(props.eventId, list.status.value);
            router.push(`/Lists/${ListId}`);
        });

    const showExistingListModal = ref(false);
    const contactLists = ref<ContactListType[] | null>(null);
    const selectedListId = ref<string | null>(null);
    const listOptions = computed(() => (contactLists.value ?? []).map((l) => ({ ListId: l.ListId, Name: l.Name })) as Record<string, unknown>[]);

    async function openExistingListModal() {
        try {
            contactLists.value ??= await loadLists();
        } catch {
            bsAlert({ type: AlertTypes.Danger });
            return;
        }
        selectedListId.value = null;
        showExistingListModal.value = true;
    }

    function exportToExistingList() {
        const targetListId = selectedListId.value;
        if (!targetListId) return;
        showExistingListModal.value = false;

        runExport(async () => {
            const { ListId } = await guestListApi.exportParticipantsToExistingList(props.eventId, list.status.value, targetListId);
            router.push(`/Lists/${ListId}`);
        });
    }

    // --- Contacts ---

    async function addToGuestList(contactData: ContactData) {
        const contact = contactData.contact;
        busy.value = true;
        try {
            if (await guestListApi.participantExistsForContact(props.eventId, contact.ContactId)) {
                bsAlert({
                    type: AlertTypes.Danger,
                    title: $Resources.EventAddParticipantAlreadyExistsAlertHeading,
                    content: $Resources.EventAddParticipantAlreadyExistsAlertText,
                });
                return;
            }

            await guestListApi.addNewParticipant(props.eventId, { Email: contact.Email, SmsNumber: contact.SmsNumber });
            bsAlert({ title: $Resources.AddedNewParticipantHeading, content: $Resources.AddedNewParticipantText });
            list.reloadCurrent();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.UnableToAddNewParticipantHeading, content: $Resources.UnableToAddNewParticipantText });
        } finally {
            busy.value = false;
            contactModalId.value = null;
        }
    }

    // --- Survey response ---

    const responseRow = ref<GuestRow | null>(null);
    const eventEnded = computed(() => event.value?.Status === EventStatusNames.Ended);

    // Kept apart from responseRow, which is cleared before the modal has finished closing when a contact is opened.
    let responseParticipantId: string | null = null;

    function showResponse(row: GuestRow) {
        responseParticipantId = row.id;
        responseRow.value = row;
    }

    // Only the edited guest's group is re-fetched, instead of the legacy closeResponse full reload.
    function onResponseClosed(edited: boolean) {
        responseRow.value = null;
        if (edited && responseParticipantId) list.refreshGroups([responseParticipantId]);
    }

    function showContactFromResponse(contactId: string) {
        responseRow.value = null;
        contactModalId.value = contactId;
    }

    function onContactModalClosed(hasChanged: boolean) {
        const contactId = contactModalId.value;
        contactModalId.value = null;
        if (hasChanged && contactId) list.refreshContact(contactId);
    }

    // --- Navigation ---

    const showPublicGuestList = () => router.push({ name: 'EventEditorLive', params: { eventId: props.eventId } });
    const goToInvitations = () => router.push({ name: 'EventEditorIssues', params: { eventId: props.eventId } });
</script>

<template>
    <h2 class="m-0-t">{{ $Resources.EventGuestlistHeading }}</h2>

    <div v-show="participantTotal > 0">
        <div class="m-lg-b">
            <EventListItemFullStatus
                :eventId="eventId"
                :requireAttestation="requireAttestation"
                :reloadKey="list.countsVersion.value"
                @loaded="onStatusLoaded" />
        </div>

        <BsAlert
            v-if="!canReadContacts"
            :type="AlertTypes.Info"
            :dismissible="false"
            :title="$Resources.AlertViewContactPermissionHeading"
            :content="$Resources.AlertViewContactPermissionBody" />

        <FeatureLock v-else feature="Events.GuestList" hideWhenLocked>
            <GuestListToolbar
                :status="list.status.value"
                :requireAttestation="requireAttestation"
                :attestationPendingCount="attestationPendingTotal"
                v-model:disableStatusNotifications="disableStatusNotifications"
                @update:status="list.setStatus"
                @resendAllAttestations="confirmResendAllAttestations" />

            <GuestListTable
                v-model:freeText="list.freeText.value"
                :rows="list.rows.value"
                :selectedIds="list.selectedIds.value"
                v-model:checkboxesToggled="list.checkboxesToggled.value"
                :shownCount="list.participants.value.length"
                :totalCount="list.totalCount.value"
                :matchingCount="list.matchingCount.value"
                :showCheckboxes="list.showCheckboxes.value"
                :allLoaded="list.allLoaded.value"
                :loading="list.loading.value || list.childrenLoading.value"
                :requireAttestation="requireAttestation"
                :orderBy="list.orderBy.value"
                :orderDirection="list.orderDirection.value"
                :selectedAcceptedCount="list.selectedAcceptedCount.value"
                :acceptedTotal="bulkAcceptedCount"
                :exportDisabled="list.participants.value.length === 0"
                :busy="busy"
                @toggleSelected="list.toggleSelected"
                @setSelected="list.setSelected"
                @loadMore="list.loadMore"
                @order="list.setOrder"
                @addParticipant="contactModalId = 'new'"
                @showPublicGuestList="showPublicGuestList"
                @exportToFile="exportToFile"
                @exportToCsv="exportToCsv"
                @exportToNewList="exportToNewList"
                @exportToExistingList="openExistingListModal"
                @bulkToAttended="confirmBulkToAttended"
                @selectedToAttended="selectedToAttended"
                @updateStatus="updateStatus"
                @showResponse="showResponse"
                @showProfile="(contactId) => (contactModalId = contactId)"
                @resendConfirmation="confirmResendConfirmation"
                @resendAttestation="confirmResendAttestation" />
        </FeatureLock>
    </div>

    <EmptyState
        v-if="statusLoaded && participantTotal === 0 && !list.loading.value"
        class="m-lg-b"
        :title="$Resources.EventGuestListNoParticipantsHeading"
        :body="$Resources.EventGuestListNoParticipantsText">
        <template #actions>
            <div class="form-group m-sm-b">
                <button type="button" class="btn btn-info" @click="goToInvitations">
                    {{ $Resources.EventGuestListNoParticipantsToInvitationsTabBtnText }}
                    <i class="fa fa-chevron-right" aria-hidden="true"></i>
                </button>
            </div>
            <FeatureLock feature="Events.GuestList" hideWhenLocked>
                <div class="form-group m-sm-b">
                    <button type="button" class="btn btn-subtle" @click="contactModalId = 'new'">
                        <span class="glyphicon glyphicon-user m-xs-r"></span>
                        {{ $Resources.EventGuestListNoParticipantsAddManuallyBtnText }}
                    </button>
                </div>
            </FeatureLock>
        </template>
    </EmptyState>

    <Modal :show="!!pendingConfirm" :title="pendingConfirm?.title" size="sm" @close="pendingConfirm = null">
        <template #content>
            <p v-if="pendingConfirm?.text">{{ pendingConfirm.text }}</p>
            <p v-if="pendingConfirm?.participant">
                <strong v-if="participantName(pendingConfirm.participant)" class="d-block">{{ participantName(pendingConfirm.participant) }}</strong>
                {{ pendingConfirm.participant.Email }}
            </p>
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" :disabled="busy" @click="confirmPending">{{ $Resources.ConfirmButton }}</button>
        </template>
    </Modal>

    <Modal :show="showExistingListModal" :title="$Resources.ExportToExistingListTitle" @close="showExistingListModal = false">
        <template #content>
            <SingleSelect
                :items="listOptions"
                :modelValue="selectedListId"
                selectProp="ListId"
                displayProps="Name"
                :label="$Resources.SelectListLabel"
                @update:modelValue="(val) => (selectedListId = val)" />
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" :disabled="!selectedListId || busy" @click="exportToExistingList">
                {{ $Resources.ExportButton }}
            </button>
        </template>
    </Modal>

    <GuestResponseModal :row="responseRow" :eventEnded="eventEnded" @close="onResponseClosed" @showContact="showContactFromResponse" />

    <ContactModal :contactId="contactModalId" :updateIdAfterCreated="false" @created="addToGuestList" @closed="onContactModalClosed" />
</template>

